import { useState, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import api from '../../../api/client';

export default function MartsPage() {
  const [marts,   setMarts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // Selected mart + its query result.
  const [selected,    setSelected]    = useState(null);
  const [result,      setResult]      = useState(null);
  const [queryLoad,   setQueryLoad]   = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/prism/marts');
      setMarts(data.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function runQuery(mart) {
    setQueryLoad(true);
    setError(null);
    try {
      // Default query: no user-selected dims/measures — derive dims from the
      // mart's own declaration so grouped marts chart, and let the backend
      // default the measures (all declared measures summed).
      const dims = mart.definition?.dims || [];
      const { data } = await api.post(`/prism/marts/${mart.id}/query`, { dims });
      setResult(data.data?.result || null);
    } catch (e) {
      setResult(null);
      setError(e.response?.data?.message || e.message);
    } finally {
      setQueryLoad(false);
    }
  }

  function selectMart(mart) {
    if (selected && selected.id === mart.id) {
      setSelected(null);
      setResult(null);
      return;
    }
    setSelected(mart);
    setResult(null);
    runQuery(mart);
  }

  async function refresh(mart) {
    setRefreshingId(mart.id);
    setError(null);
    try {
      await api.post(`/prism/marts/${mart.id}/refresh`);
      await load();
      if (selected && selected.id === mart.id) await runQuery(mart);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setRefreshingId(null);
    }
  }

  async function exportCsv(mart) {
    setError(null);
    try {
      // Bearer-authenticated download — fetch as a blob then trigger the save.
      const res = await api.get(`/prism/marts/${mart.id}/export`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mart-${mart.key}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  }

  // ── Chart derivation ────────────────────────────────────────────────────────
  // Result columns = [...dims, ...measureAliases]. Dim columns are those the
  // selected mart declares as dims; the rest are measures.
  const columns   = result?.columns || [];
  const rows      = result?.rows || [];
  const declared  = selected?.definition?.dims || [];
  const dimCols   = columns.filter((c) => declared.includes(c));
  const measCols  = columns.filter((c) => !dimCols.includes(c));
  const showChart = dimCols.length >= 1 && measCols.length >= 1 && rows.length >= 1;
  const xKey      = dimCols[0];
  const yKey      = measCols[0];

  return (
    <div data-testid="prism-marts-page" data-loading={loading ? 'true' : 'false'}>
      <h2>Data Marts</h2>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      {loading ? (
        <p>Loading...</p>
      ) : !marts.length ? (
        <p>No data marts defined.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {marts.map((m) => (
            <div
              key={m.id}
              data-testid={`prism-mart-${m.key}`}
              onClick={() => selectMart(m)}
              style={{
                cursor: 'pointer',
                border: '1px solid #ccc',
                borderRadius: 8,
                padding: '0.75rem 1rem',
                background: selected && selected.id === m.id ? '#eef' : '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '0.5rem',
              }}
            >
              <div>
                <strong>{m.name}</strong>{' '}
                <span style={{ color: '#666' }}>({m.key})</span>
                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                  domain: {m.domain}
                  {m.refreshed_at ? ` · refreshed ${new Date(m.refreshed_at).toLocaleString()}` : ' · never refreshed'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  data-testid="prism-refresh"
                  onClick={(e) => { e.stopPropagation(); refresh(m); }}
                  disabled={refreshingId === m.id}
                >
                  {refreshingId === m.id ? 'Refreshing...' : 'Refresh'}
                </button>
                <button
                  data-testid="prism-export"
                  onClick={(e) => { e.stopPropagation(); exportCsv(m); }}
                >
                  Export CSV
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Selected-mart results */}
      {selected && (
        <div
          data-testid="prism-mart-result"
          style={{ marginTop: '1.25rem', border: '1px solid #ccc', borderRadius: 8, padding: '1rem' }}
        >
          <h3 style={{ marginTop: 0 }}>{selected.name} — results</h3>

          {queryLoad ? (
            <p>Running query...</p>
          ) : !result ? (
            <p>No results.</p>
          ) : (
            <>
              {showChart && (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={rows}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={xKey} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey={yKey} fill="#2563eb" />
                  </BarChart>
                </ResponsiveContainer>
              )}

              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: showChart ? '1rem' : 0 }}>
                <thead>
                  <tr>
                    {columns.map((c) => <th key={c} style={{ textAlign: 'left' }}>{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} data-testid="prism-mart-result-row">
                      {columns.map((c) => <td key={c}>{String(r[c] ?? '')}</td>)}
                    </tr>
                  ))}
                  {!rows.length && <tr><td colSpan={columns.length || 1}>No rows — try refreshing the mart.</td></tr>}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}
